import * as React from 'react';
import { EPageTypes, EPlatformTypes, ISimpleCallProps, SimpleCall } from '@cian/frontend-newbuilding-call-component';

import { SinglePhoneComponent, TCallButtonSize } from '../../components/SinglePhoneComponent';
import { IBuilder, ISeller } from '../../types/newbuildingsCompare';
import { TGallerySiteType } from '../../types/analytics';
import { useApplicationContext } from '../../utils/applicationContext';
import { TSellerType, trackOpenContacts, trackShowPhone, trackShowPhoneGallery } from './tracking';

type TCallBuilder = ISimpleCallProps['builders'][number];
type TCallSeller = ISimpleCallProps['sellers'][number];

const PLACE_TYPE = 'newbuildingSummary';

export interface INewbuildingCallButtonProps {
  newbuildingId: number;
  url: string;
  builders: IBuilder[];
  sellers: ISeller[];
  size: TCallButtonSize;
  singlePhoneButtonText?: string;
  showPhoneButtonIcon?: boolean;
  gallerySiteType?: TGallerySiteType;
  onShowPhone?(): void;
}

export const NewbuildingCallButton: React.FC<INewbuildingCallButtonProps> = ({
  newbuildingId,
  url,
  builders,
  sellers,
  size,
  singlePhoneButtonText = 'Показать телефон',
  showPhoneButtonIcon = false,
  gallerySiteType,
  onShowPhone,
}) => {
  const { httpApi, logger } = useApplicationContext();

  const { callBuilders, callSellers, sellerTypeByRealtyUserId } = React.useMemo(() => {
    const map = new Map<number, TSellerType>();

    const mappedBuilders: TCallBuilder[] = builders
      .filter(builder => builder.phone)
      .map(builder => {
        map.set(builder.id, 'builders');

        return {
          id: builder.id,
          realtyUserId: builder.id, // Внутри либы используется просто как уникальное значение
          isFichering: false,
          logoUrl: builder.logoUrl,
          name: builder.name,
          phone: builder.phone,
          showPhone: true,
          offersForSaleText: builder.offersForSale,
          dynamicCalltracking: { siteBlockId: builder.newbuildingDynamicCalltracking?.siteBlockId ?? null },
        };
      });

    const mappedSellers: TCallSeller[] = sellers
      .filter((seller): seller is ISeller & { phone: NonNullable<ISeller['phone']> } => Boolean(seller.phone))
      .map((seller, index) => {
        // realtyUserId в либе используется просто как уникальное значение, используем index + 1
        const realtyUserId = index + 1;
        map.set(realtyUserId, 'agents');

        return {
          realtyUserId,
          isLeadFactory: false,
          // Логотип продавца в либе называется avatarUrl
          avatarUrl: seller.logoUrl,
          name: seller.name,
          phone: seller.phone,
          showPhone: true,
          offersForSaleText: seller.offersForSale,
          dynamicCalltracking: { siteBlockId: seller.newbuildingDynamicCalltracking?.siteBlockId ?? null },
        };
      });

    return { callBuilders: mappedBuilders, callSellers: mappedSellers, sellerTypeByRealtyUserId: map };
  }, [builders, sellers]);

  const PhoneComponent = React.useMemo<ISimpleCallProps['SinglePhoneComponent']>(
    () =>
      function SinglePhone({ phone }) {
        return <SinglePhoneComponent phone={phone} size={size} isGallery={Boolean(gallerySiteType)} />;
      },
    [size],
  );

  const handleOpenContacts = React.useCallback(() => {
    trackOpenContacts({ newbuildingId, url });
  }, [newbuildingId, url]);

  const handleShowPhone = React.useCallback(
    ({ realtyUserId }: { realtyUserId: number }) => {
      if (onShowPhone) {
        onShowPhone();

        return;
      }

      const sellerType = sellerTypeByRealtyUserId.get(realtyUserId);

      if (gallerySiteType) {
        trackShowPhoneGallery({ newbuildingId, url, siteType: gallerySiteType });
      } else {
        trackShowPhone({ newbuildingId, url, sellerType });
      }
    },
    [gallerySiteType, newbuildingId, onShowPhone, url, sellerTypeByRealtyUserId],
  );

  return (
    <SimpleCall
      builders={callBuilders}
      sellers={callSellers}
      httpApi={httpApi}
      logger={logger}
      pageType={EPageTypes.OfferComparison}
      platformType={EPlatformTypes.WebDesktop}
      placeType={PLACE_TYPE}
      SinglePhoneComponent={PhoneComponent}
      singlePhoneButtonText={singlePhoneButtonText}
      showPhoneButtonIcon={showPhoneButtonIcon}
      showPhoneStyles={{ size, theme: 'main_primary' }}
      openContactsStyles={{ size, theme: 'main_primary' }}
      onOpenContactsClick={handleOpenContacts}
      onShowPhoneClick={handleShowPhone}
    />
  );
};
